import { FC } from 'react';
import { Helmet } from 'react-helmet-async';
import { useIntl } from 'react-intl';
import { generatePath, Link, useLocation, useNavigate } from 'react-router-dom';
import cn from 'classnames';
import { isEmpty, size } from 'lodash';
import { useBoolean } from 'usehooks-ts';
import { faFolders } from '@fortawesome/pro-duotone-svg-icons';
import { faPlusCircle, faSearch } from '@fortawesome/pro-solid-svg-icons';
import {
  Card,
  createBemBlockBuilder,
  Loading,
  Tooltip,
} from '@ebsco-ui/ebsco-ui';

import {
  BrowseSearch,
  EmptyRecordsMessage,
  Icon,
  MoreToolsDropdown,
  PageErrorMessage,
  PageTitle,
  RECORD_TOOLS,
  RedirectToLoginButton,
  ResultCountWithSorting,
} from '@app/components';
import { useAppContext } from '@app/contexts';
import { useProjectsFetch, useQueryParams } from '@app/hooks';
import { generateRouteParams } from '@app/utils';
import { resultCountMessages, sharedMessages } from '@app/translations';
import { Route } from '@app/constants';

import { useDashboardListSorting } from '../hooks/useDashboardListSorting';
import { CreateNewProjectModal } from '../ProjectModals';

import './ProjectsPage.scss';

const cnBem = createBemBlockBuilder(['projectsPage']);

export const ProjectsPage: FC = () => {
  const { $t, formatNumber } = useIntl();
  const { isLoggedIn } = useAppContext();
  const { sortOptions, sortDirection, handleSortChange } =
    useDashboardListSorting();
  const queryParams = useQueryParams();
  const location = useLocation();
  const navigate = useNavigate();
  const {
    data: projects,
    isFetching,
    isError,
    refetch,
  } = useProjectsFetch({
    search: queryParams.search as string,
    sortDirection,
  });
  const {
    value: isCreateProjectModalOpen,
    setTrue: openCreateProjectModal,
    setFalse: closeCreateProjectModal,
  } = useBoolean(false);

  const pageTitle = $t(sharedMessages.projects);

  const renderPageContent = () => {
    if (isFetching) {
      return (
        <div data-auto="spinner">
          <Loading loadingMessage={$t(sharedMessages.loadingMessage)} />
        </div>
      );
    }

    if (isError) {
      return <PageErrorMessage onClick={refetch} />;
    }

    if (!isLoggedIn) {
      return (
        <EmptyRecordsMessage
          icon={faFolders}
          message={$t({
            id: 'projects.signInToAccessProjects',
            defaultMessage: 'Please sign in to access projects.',
          })}
        >
          <RedirectToLoginButton />
        </EmptyRecordsMessage>
      );
    }

    if (isEmpty(projects)) {
      const message = !queryParams.search
        ? $t({
            id: 'projects.emptyStateTitle',
            defaultMessage: 'You currently have no projects.',
          })
        : $t(
            {
              id: 'projects.emptySearchTitle',
              defaultMessage: 'No projects found for - "{value}"',
            },
            { value: queryParams.search as string }
          );

      return (
        <EmptyRecordsMessage icon={faFolders} message={message}>
          <Link to={Route.search}>
            <Icon icon={faSearch}>{$t(sharedMessages.startANewSearch)}</Icon>
          </Link>
        </EmptyRecordsMessage>
      );
    }

    return projects?.map(({ id, name, totalRecords }) => (
      <Card key={id} border="bottom" className={cnBem('__project')}>
        <div data-auto="record-card">
          <div>
            <Link
              to={generatePath(Route.projectsDetailsRecords, {
                id,
              })}
              key={name}
              className={cnBem('__projectLink')}
            >
              {name}
            </Link>
            <p
              className={cnBem('__projectItemsCount')}
              data-auto="record-card-items-count"
            >
              {$t(
                {
                  id: 'projects.itemsCount',
                  defaultMessage:
                    '{value, plural, one {# item} other {# items}}',
                },
                { value: formatNumber(totalRecords) }
              )}
            </p>
          </div>
          <MoreToolsDropdown tools={[RECORD_TOOLS.delete]} />
        </div>
      </Card>
    ));
  };

  const handleSearch = (search: string) =>
    navigate(
      generateRouteParams({
        pathname: location.pathname,
        ...(search && {
          query: {
            ...queryParams,
            search,
          },
        }),
      })
    );

  return (
    <>
      <Helmet>
        <title>
          {$t({
            id: 'projects.documentTitle',
            defaultMessage: 'Projects - EBSCO Locate',
          })}
        </title>
      </Helmet>
      <div className={cn(cnBem(), 'container')} data-auto="projects-container">
        <div className={cnBem('__titleContainer')}>
          <PageTitle>{$t(sharedMessages.projects)}</PageTitle>
          <Tooltip
            title={$t(sharedMessages.createNewProject)}
            placement="right"
          >
            <button
              aria-label={$t(sharedMessages.createNewProject)}
              type="button"
              className={cnBem('__newProjectButton')}
              onClick={openCreateProjectModal}
            >
              <Icon icon={faPlusCircle} />
            </button>
          </Tooltip>
        </div>
        <BrowseSearch
          label={pageTitle}
          placeholder={$t({
            id: 'projects.searchBarPlaceholder',
            defaultMessage: 'Search by project title or tag...',
          })}
          onSearch={handleSearch}
        />
        <CreateNewProjectModal
          isOpen={isCreateProjectModalOpen}
          onClose={closeCreateProjectModal}
        />
        <ResultCountWithSorting
          recordsCount={size(projects)}
          countMessage={resultCountMessages.numberOfProjects}
          sortField="name"
          sortDirection={sortDirection}
          sortOptions={sortOptions}
          onSortChange={handleSortChange}
        />
        {renderPageContent()}
      </div>
    </>
  );
};
